import { useId, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Plus } from "lucide-react";
import {
  fetchDayBook,
  useCreateJournal,
  useJournal,
  useJournals,
  useLedgerAccounts,
  useRepostJournal,
  useReverseJournal,
  type EntryKind,
  type JournalRow,
  type Period,
} from "@/api/books";
import { RecordLink } from "@/components/books/BooksLink";
import { parseSource } from "@/components/books/links";
import { PeriodPicker, thisFy } from "@/components/books/PeriodPicker";
import { Alert } from "@/components/ui/alert";
import { JournalForm } from "@/components/forms/JournalForm";
import { ReasonForm } from "@/components/forms/ReasonForm";
import { RequestError } from "@/components/RequestError";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { SelectField, TextField } from "@/components/ui/field";
import { todayIso } from "@/lib/dates";
import { saveBlob } from "@/lib/files";
import { formatDate, formatInr, formatMoney } from "@/lib/format";

type Panel =
  | { kind: "new" }
  | { kind: "reverse"; entry: JournalRow }
  | { kind: "repost"; entry: JournalRow }
  | null;

const KIND_LABEL: Record<EntryKind, string> = {
  auto: "Derived",
  manual: "Manual",
  reversal: "Reversal",
  opening: "Opening",
};
const KIND_TONE: Record<EntryKind, "secondary" | "default" | "warning" | "muted"> = {
  auto: "secondary",
  manual: "default",
  reversal: "warning",
  opening: "muted",
};
const KINDS: { value: string; label: string }[] = [
  { value: "", label: "All kinds" },
  ...(Object.keys(KIND_LABEL) as EntryKind[]).map((k) => ({ value: k, label: KIND_LABEL[k] })),
];
const NUM = "whitespace-nowrap px-4 py-2 text-right tabular-nums";

function EntryLines({ id }: { id: string }) {
  const entry = useJournal(id);
  if (!entry.data) return <RequestError error={entry.error} />;
  return (
    <table className="data-table">
      <tbody>
        {entry.data.lines.map((line) => (
          <tr key={line.id} className="border-t">
            <td className="px-4 py-1 text-muted-foreground">{line.account_code}</td>
            <td className="px-4 py-1">
              {line.account_name}
              {line.party_label && (
                <span className="text-muted-foreground"> · {line.party_label}</span>
              )}
              {line.fx_amount && line.fx_currency && (
                <span className="block text-xs text-muted-foreground">
                  {formatMoney(line.fx_amount, line.fx_currency)} @ {line.fx_rate}
                </span>
              )}
              {line.memo && <span className="block text-xs text-muted-foreground">{line.memo}</span>}
            </td>
            <td className={NUM}>{Number(line.debit_inr) ? formatInr(line.debit_inr) : ""}</td>
            <td className={NUM}>{Number(line.credit_inr) ? formatInr(line.credit_inr) : ""}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The banner over one record's entries, opened from the record ("In the books"). */
function SourceBanner({ rows, onShowAll }: { rows: JournalRow[] | undefined; onShowAll: () => void }) {
  const label = rows?.find((row) => row.source_label)?.source_label;
  return (
    <Alert
      title={label ? `Showing the entries for ${label}` : "Showing the entries for one record"}
      action={
        <Button size="sm" variant="outline" onClick={onShowAll}>
          Show all entries
        </Button>
      }
    >
      The entries this record made (a bill also when it was paid, an invoice its write-off), with
      a reversal for each time it changed or was voided. A payment is a record of its own.
    </Alert>
  );
}

export function JournalsPanel() {
  const id = useId();
  const [params, setParams] = useSearchParams();
  const source = parseSource(params.get("source"));
  const [period, setPeriod] = useState<Period>(thisFy());
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [panel, setPanel] = useState<Panel>(null);
  const [downloading, setDownloading] = useState(false);
  const journals = useJournals(
    source
      ? { sourceId: source.id }
      : {
          from: period.from,
          to: period.to,
          kind: (kind || undefined) as EntryKind | undefined,
          q: q || undefined,
        },
  );
  const accounts = useLedgerAccounts(false);
  const create = useCreateJournal();
  const reverse = useReverseJournal();
  const repost = useRepostJournal();

  async function download(): Promise<void> {
    setDownloading(true);
    try {
      const file = await fetchDayBook(period);
      saveBlob(file.blob, file.filename);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {source && (
        <SourceBanner
          rows={journals.data}
          onShowAll={() => setParams({ tab: "journals" }, { replace: true })}
        />
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        {source ? (
          <span />
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <PeriodPicker value={period} onChange={setPeriod} />
            <SelectField id={`${id}-kind`} label="Kind" value={kind} onChange={setKind} options={KINDS} />
            <TextField id={`${id}-q`} label="Search" value={q} onChange={setQ} placeholder="Narration or reference" />
          </div>
        )}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => void download()} disabled={downloading}>
            <Download className="h-4 w-4" aria-hidden />
            {downloading ? "Preparing…" : "Day book (.xlsx)"}
          </Button>
          {panel === null && (
            <Button size="sm" onClick={() => setPanel({ kind: "new" })} disabled={!accounts.data}>
              <Plus className="h-4 w-4" aria-hidden />
              New journal
            </Button>
          )}
        </div>
      </div>
      {panel?.kind === "new" && accounts.data && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>New manual entry</CardTitle>
          </CardHeader>
          <CardContent>
            <JournalForm
              accounts={accounts.data}
              onSubmit={async (input) => {
                await create.mutateAsync(input);
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "reverse" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Reverse {panel.entry.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReasonForm
              label="Why is it being reversed?"
              hint="A mirror-image entry is posted today; the original stays on record."
              submitLabel="Reverse entry"
              submitVariant="destructive"
              onSubmit={async (reason) => {
                await reverse.mutateAsync({ id: panel.entry.id, on: todayIso(), reason });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      )}
      {panel?.kind === "repost" && (
        <Card className="border-primary/40">
          <CardHeader>
            <CardTitle>Repost {panel.entry.label}</CardTitle>
          </CardHeader>
          <CardContent>
            <ReasonForm
              label="Why is it being reposted?"
              hint="The entry is reversed today and derived again from its record with today's rates."
              submitLabel="Repost entry"
              onSubmit={async (reason) => {
                await repost.mutateAsync({ id: panel.entry.id, reason });
                setPanel(null);
              }}
              onCancel={() => setPanel(null)}
            />
          </CardContent>
        </Card>
      )}
      <RequestError error={journals.error} />
      {journals.data && journals.data.length === 0 ? (
        <EmptyState
          title={source ? "No entries for this record yet" : "No entries in this period"}
          description={
            source ? "It goes into the books at the next posting, or when Books is opened." : undefined
          }
        />
      ) : (
        <div className="table-frame">
          <table className="data-table">
            <thead className="border-b bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-2 font-medium">Entry</th>
                <th className="px-4 py-2 font-medium">Date</th>
                <th className="px-4 py-2 font-medium">Kind</th>
                <th className="px-4 py-2 font-medium">Narration</th>
                <th className="px-4 py-2 text-right font-medium">Total</th>
                <th className="px-4 py-2">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {(journals.data ?? []).map((row) => (
                <tr key={row.id} className="border-b align-top last:border-0">
                  <td className="whitespace-nowrap px-4 py-2 font-medium">
                    <button
                      type="button"
                      className="text-primary hover:underline"
                      onClick={() => setOpen(open === row.id ? null : row.id)}
                    >
                      {row.label}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-4 py-2">{formatDate(row.entry_date)}</td>
                  <td className="px-4 py-2">
                    <Badge variant={KIND_TONE[row.kind]}>{KIND_LABEL[row.kind]}</Badge>
                    {row.superseded_by_entry_id && <Badge variant="muted">reversed</Badge>}
                  </td>
                  <td className="px-4 py-2">
                    {row.narration}
                    {row.source_label && (
                      <RecordLink source={row} label={row.source_label} className="block w-fit text-xs" />
                    )}
                    {open === row.id && (
                      <div className="mt-2 rounded border">
                        <EntryLines id={row.id} />
                      </div>
                    )}
                  </td>
                  <td className={NUM}>{formatInr(row.total_inr)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {!row.superseded_by_entry_id && (row.kind === "manual" || row.kind === "opening") && (
                      <Button size="sm" variant="ghost" onClick={() => setPanel({ kind: "reverse", entry: row })}>
                        Reverse
                      </Button>
                    )}
                    {!row.superseded_by_entry_id && row.kind === "auto" && (
                      <Button size="sm" variant="ghost" onClick={() => setPanel({ kind: "repost", entry: row })}>
                        Repost
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
